import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../api/client';

export interface CancelJobVars {
  jobId: string;
  /** For the toast — the job number the dispatcher is looking at. */
  jobNumber?: string;
}

/**
 * Cancel a job from the dispatch board's card menu.
 *
 * This replaces a bare `await api.dispatch.updateJob(job.id, { status:
 * 'cancelled' })` inside the menu's `handleAction`, with no catch anywhere
 * above it. A refusal — a 403 from the role gate, a 409, a dropped connection —
 * rejected into nothing: the menu closed, the card stayed exactly where it was,
 * and the dispatcher had every reason to believe the job was cancelled. The
 * same defect the phase clocks had, in the same menu, and #674 fixed only that
 * half; see usePhaseClockMutation, which this mirrors deliberately.
 *
 * The equipment keys are invalidated beside the board because cancelling a job
 * releases its reserved gear server-side.
 *
 * NO SUCCESS TOAST. The card visibly moves to Cancelled on the refetch, which
 * is the answer; a toast on top of a state the dispatcher just watched change
 * is noise. A refusal has nothing to show, which is exactly why it gets one.
 */
export function useCancelJobMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId }: CancelJobVars) =>
      api.dispatch.updateJob(jobId, { status: 'cancelled' }),
    onError: (err, { jobNumber }) => {
      toast.error(
        getApiErrorMessage(err, `Could not cancel${jobNumber ? ` ${jobNumber}` : ' the job'}.`),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-cert-summary'] });
      queryClient.invalidateQueries({ queryKey: ['home-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-report'] });
    },
  });
}
